import { useEffect, useState } from "react";
import type { IntelligenceItem } from "@yala/shared";
import { safeUrl } from "../lib/safeUrl";
import { PanelHeader } from './PanelHeader';
import { useT } from "../hooks/useLang";
import { ago } from "../lib/time";
import { scoreColor } from "../lib/newsDesk";
import { API_BASE } from "../lib/apiBase";

/** Last-7-days volume bucket from /api/news/stats. */
interface VolumeDay {
  day: string;
  count: number;
}

/**
 * Self-fetch the 7-day news-volume series from /api/news/stats. Returns an
 * empty array on any failure so the panel never crashes on missing data.
 */
function useNewsVolume(apiBase: string): VolumeDay[] {
  const [byDay, setByDay] = useState<VolumeDay[]>([]);
  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    fetch(`${apiBase}/api/news/stats`, { signal: ctrl.signal, cache: "no-store" })
      .then((res) => (res.ok ? res.json() : null))
      .then((json: unknown) => {
        if (cancelled || !json || typeof json !== "object") return;
        const raw = (json as { byDay?: unknown }).byDay;
        if (!Array.isArray(raw)) return;
        const clean = raw
          .filter((d): d is VolumeDay =>
            !!d && typeof d === "object" &&
            typeof (d as VolumeDay).day === "string" &&
            typeof (d as VolumeDay).count === "number")
          .map((d) => ({ day: d.day, count: d.count }));
        setByDay(clean);
      })
      .catch(() => { /* offline / 503 on Workers runtime — render without the viz */ });
    return () => { cancelled = true; ctrl.abort(); };
  }, [apiBase]);
  return byDay;
}

/** Compact 7-day news-volume sparkline. Hairline bars, tabular-nums, 11px. */
function NewsVolumeSpark({ byDay }: { byDay: VolumeDay[] }) {
  if (byDay.length === 0) return null;
  const counts = byDay.map((d) => d.count);
  const peak = Math.max(...counts, 1);
  const total = counts.reduce((a, b) => a + b, 0);
  const w = 84;
  const h = 16;
  const gap = 1.5;
  const bw = (w - gap * (byDay.length - 1)) / byDay.length;
  return (
    <span className="news-vol" title={`${total} stories archived over the last 7 days`}>
      <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} aria-hidden="true" style={{ display: "block" }}>
        {byDay.map((d, i) => {
          const bh = Math.max(0.75, (d.count / peak) * h);
          const x = i * (bw + gap);
          const isLast = i === byDay.length - 1;
          return (
            <rect key={d.day} x={x.toFixed(2)} y={(h - bh).toFixed(2)}
                  width={bw.toFixed(2)} height={bh.toFixed(2)}
                  fill={isLast ? "var(--ink)" : "var(--ink-3)"} />
          );
        })}
      </svg>
      <span className="news-vol-label mono">7D · {total}</span>
    </span>
  );
}

// Mayor's Action tag legend — 2-char code → short label, colour, glyph
// Colours use CSS token variables defined in tokens.css.
const ACTION_TAG: Record<string, { label: string; color: string; glyph: string; do: string }> = {
  EM: { label: "Emergency",        color: "var(--bad)",         glyph: "▲", do: "go to scene / coordinate response" },
  FU: { label: "Funeral",          color: "var(--tag-funeral)", glyph: "✚", do: "attend or send wreath" },
  PO: { label: "Police friction",  color: "var(--tag-police)",  glyph: "◆", do: "consider mediation" },
  HO: { label: "Honour",           color: "var(--good)",        glyph: "★", do: "congratulate" },
  FE: { label: "Festival",         color: "var(--tag-festival)",glyph: "✦", do: "attend opening" },
  IN: { label: "Infrastructure",   color: "var(--data)",        glyph: "▣", do: "chase department" },
  BZ: { label: "EEC / Business",   color: "var(--accent)",      glyph: "◢", do: "attend signing" },
  PU: { label: "Public health",    color: "var(--tag-health)",  glyph: "✚", do: "visit / congratulate staff" },
};

interface Props {
  items: IntelligenceItem[];
  loading: boolean;
  ageMinutes?: number;
  onRefresh?: () => void;
  /** Override API host for the self-fetched 7-day volume spark. Defaults to the
   *  resolved production/dev base; optional so App.tsx needs no change. */
  apiBase?: string;
}

export function NewsDesk({ items, loading, ageMinutes, onRefresh, apiBase = API_BASE }: Props) {
  const t = useT();
  const volume = useNewsVolume(apiBase);
  if (loading && items.length === 0) {
    return (
      <div className="col">
        <div className="eyebrow">MAYOR&apos;S DESK // YALA</div>
        <div className="skeleton" style={{ height: 10, marginTop: 8 }} />
        <div className="skeleton" style={{ height: 10, width: "80%", marginTop: 6 }} />
        <div className="skeleton" style={{ height: 10, width: "60%", marginTop: 6 }} />
      </div>
    );
  }

  // Split into actionable vs general — actionable items first
  const actionable = items.filter((it) => it.tags && it.tags.length > 0).slice(0, 8);
  const others = items.filter((it) => !it.tags || it.tags.length === 0).slice(0, 6);
  const visible = [...actionable, ...others].slice(0, 14);

  return (
    <div className="col">
      <PanelHeader
        title={t("MAYOR'S DESK // YALA", "โต๊ะนายกเทศมนตรี // ยะลา")}
        ageMinutes={ageMinutes}
        actions={
          <span className="news-count-cluster">
            <NewsVolumeSpark byDay={volume} />
            <span className="news-count mono" title={`${actionable.length} actionable / ${items.length} total`}>
              {String(actionable.length).padStart(2, "0")}<span style={{ opacity: 0.5 }}>/{String(items.length).padStart(3, "0")}</span>
            </span>
            {onRefresh && (
              <button
                type="button"
                className="news-refresh mono"
                onClick={onRefresh}
                disabled={loading}
                title={ageMinutes != null ? `Refreshed ${ageMinutes}m ago — click to refresh` : "Refresh"}
                aria-label="Refresh news"
              >
                {loading ? "…" : "↻"}
              </button>
            )}
          </span>
        }
      />
      <div>
        {visible.map((it, i) => (
          <a key={it.id} href={safeUrl(it.sourceUrl) ?? undefined} target="_blank" rel="noreferrer noopener" className="news-item">
            <div className="news-header">
              <span className="news-ref mono">[{String(i + 1).padStart(3, "0")}]</span>
              {(it.tags ?? []).map((t) => {
                const a = ACTION_TAG[t];
                if (!a) return null;
                return (
                  <span key={t} className="eyebrow mono action-chip" title={`${a.label} — ${a.do}`}
                    style={{ color: a.color, borderColor: a.color }}>
                    {a.glyph} {t}
                  </span>
                );
              })}
              <span className="news-score mono" style={{ color: scoreColor(it.score), marginLeft: "auto" }}>
                REL·{it.score}
              </span>
            </div>
            <div className="title">{it.title}</div>
            <div className="meta">
              <span>{it.source.toUpperCase()}</span>
              <span>·</span>
              <span className="mono">{ago(it.publishedAt)}</span>
            </div>
          </a>
        ))}
        {visible.length === 0 && (
          <div className="caption" style={{ marginTop: 8, color: "var(--text-3)" }}>
            No headlines yet — refreshes every 3 min.
          </div>
        )}
      </div>
      {actionable.length > 0 && (
        <div className="eyebrow mono" style={{ marginTop: 8, color: "var(--text-3)" }}>
          ACTION LEGEND:&nbsp;
          ▲EM emergency · ✚FU funeral · ◆PO police · ★HO honour · ✦FE festival · ▣IN infra · ◢BZ business · ✚PU health
        </div>
      )}
    </div>
  );
}
